import { useQuery } from '@apollo/client';
import { KeyboardArrowRight } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlayContentOnDevices } from '~/api/devices';
import { IconButton } from '~/components/IconButton';
import { Tooltip } from '~/components/Tooltip';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { FolderBreadcrumbs } from '~/components/folder-breadcrumbs';
import { FolderIcon } from '~/components/folder-breadcrumbs/styles';
import { ContentGrid } from '~/components/inputs/MediaSelect/components';
import { splitSelectionIds } from '~/components/playlists/add-content/dialog';
import { FolderContextProvider, findLandingFolderId, useAppContext } from '~/contexts';
import type { DeviceBulkSelectionInput, Orientation } from '~/generated/graphql';
import { useAccessibleFolders, type FolderTreeResult } from '~/hooks/folders';
import { WizardOrientationGrid } from '~/pages/shows/components/wizard-cards';
import { PlayContentWizardDocument } from './PlayContentDialog.generated';

interface PlayContentDialogProps {
  onClose: () => void;
  onSuccess?: () => void;
  open: boolean;
  selected: DeviceBulkSelectionInput;
}

interface ShowFolderListItemProps {
  folder: FolderTreeResult;
  onClick: (folder: FolderTreeResult) => void;
}

const ShowFolderListItem = ({ folder, onClick }: ShowFolderListItemProps) => {
  const { t } = useTranslation();
  const folderName = folder.name === 'No Folder' ? t('common.fallbacks.root_folder') : folder.name;
  return (
    <ListItem
      disableGutters
      disablePadding
      secondaryAction={
        folder.children.length > 0 && !folder.isRoot ? (
          <IconButton
            label={t('common.actions.open_folder', { name: folderName })}
            onClick={(event) => {
              event.stopPropagation();
              onClick(folder);
            }}
          >
            <KeyboardArrowRight />
          </IconButton>
        ) : null
      }
    >
      <Tooltip
        title={
          !folder.canUpdate.value
            ? t('pages.devices.dialogs.play_content_configure.no_folder_permission')
            : ''
        }
      >
        <span>
          <Radio
            disabled={!folder.canUpdate.value}
            inputProps={{ 'aria-label': folderName }}
            sx={{ '&.Mui-disabled': { boxShadow: 'none', color: 'rgba(0,0,0,.2)' } }}
            value={folder.id}
          />
        </span>
      </Tooltip>
      <ListItemButton
        onClick={(event) => {
          event.stopPropagation();
          onClick(folder);
        }}
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        <ListItemIcon sx={{ display: 'flex', alignItems: 'center' }}>
          <FolderIcon />
        </ListItemIcon>
        <ListItemText primary={folderName} />
      </ListItemButton>
    </ListItem>
  );
};

export const PlayContentDialog = ({
  onClose,
  onSuccess,
  open,
  selected,
}: PlayContentDialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const [playContent, { loading }] = usePlayContentOnDevices();

  const [activeStep, setActiveStep] = useState<0 | 1 | 2>(0);
  const [pickedIds, setPickedIds] = useState<string[]>([]);
  const [orientation, setOrientation] = useState<Orientation>('LANDSCAPE');
  const [name, setName] = useState(() => {
    const prefix = t('pages.devices.dialogs.play_content_configure.default_name_prefix');
    return `${prefix} – ${new Date().toLocaleString()}`;
  });
  // Undefined until the user navigates, null for the top level
  const [navigatedFolder, setNavigatedFolder] = useState<FolderTreeResult | null>();
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');

  const { data } = useQuery(PlayContentWizardDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const { accessibleFolders, findFolder, findParent } = useAccessibleFolders(
    data?.network?.showFolderTree,
    data?.network?.showFolders,
  );

  // Open on the landing folder's parent so the landing folder itself can be selected
  const landingFolderId = data?.network
    ? findLandingFolderId(data.network.showFolderTree, data.network.showFolders)
    : undefined;
  const currentFolder =
    navigatedFolder === undefined ? findParent(landingFolderId) : navigatedFolder;

  const landingFolder = landingFolderId == null ? undefined : findFolder(landingFolderId);
  const defaultFolder = landingFolder?.canUpdate.value
    ? landingFolder
    : accessibleFolders.find((f) => f.canUpdate.value);
  const folderId = selectedFolderId || (defaultFolder ? String(defaultFolder.id) : '');

  const updateCurrentFolder = useCallback(
    (folderId: number) => {
      const folder = findFolder(folderId);
      if (!folder || folder.isRoot) {
        setNavigatedFolder(null);
        return;
      }
      setNavigatedFolder(folder);
    },
    [findFolder],
  );

  const handleFolderClick = useCallback(
    (folder: FolderTreeResult) => {
      if (folder.children.length === 0 || folder.isRoot) {
        setSelectedFolderId(String(folder.id));
      } else {
        updateCurrentFolder(folder.id);
      }
    },
    [updateCurrentFolder],
  );

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleItemClick = (id: string) => {
    setPickedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handlePlay = () => {
    if (!folderId || !name.trim()) return;
    const { contentItemIds, mediaItemIds } = splitSelectionIds(pickedIds);
    void playContent({
      variables: {
        input: {
          contentItemIds,
          mediaItemIds,
          name: name.trim(),
          orientation,
          selected,
          showFolderId: Number(folderId),
        },
      },
      // Errors surface via the mutation hook's toast — dialog stays open
      // (onCompleted only fires on success, so onClose only runs on success).
      onCompleted: () => {
        onSuccess?.();
        onClose();
      },
    });
  };

  if (data?.network == null) return null;
  const { network } = data;

  return (
    <Dialog
      PaperProps={{ sx: { minHeight: 550 } }}
      fullWidth
      maxWidth={activeStep === 0 ? 'lg' : 'md'}
      onClose={handleClose}
      open={open}
    >
      <DialogTitle onClose={handleClose}>
        {t('pages.devices.dialogs.play_content_configure.title')}
      </DialogTitle>

      <DialogContent sx={{ overflow: 'hidden' }}>
        <DialogContentTitle>
          {activeStep === 0
            ? selected.ids
              ? t('pages.devices.dialogs.play_content_configure.pick_content_description', {
                  count: selected.ids.length,
                })
              : t('pages.devices.dialogs.play_content_configure.pick_content_description_all')
            : activeStep === 1
            ? t('pages.devices.dialogs.play_content_configure.pick_orientation_description')
            : selected.ids
            ? t('pages.devices.dialogs.play_content_configure.configure_description_plural', {
                count: pickedIds.length,
                deviceCount: selected.ids.length,
              })
            : t('pages.devices.dialogs.play_content_configure.configure_description_all_plural', {
                count: pickedIds.length,
              })}
        </DialogContentTitle>

        {activeStep === 0 && (
          <FolderContextProvider
            folderTree={network.contentFolderTree}
            folders={network.contentFolders}
          >
            <ContentGrid
              maxHeight="400px"
              onClick={handleItemClick}
              rememberLastFolder
              selectedIds={pickedIds}
            />
          </FolderContextProvider>
        )}

        {activeStep === 1 && (
          <WizardOrientationGrid onClick={setOrientation} selected={orientation} />
        )}

        {activeStep === 2 && (
          <FolderContextProvider folderTree={network.showFolderTree}>
            <TextField
              autoFocus
              fullWidth
              label={t('pages.devices.dialogs.play_content_configure.name_label')}
              onChange={(e) => setName(e.target.value)}
              sx={{ mb: 2 }}
              value={name}
            />
            <Box sx={{ width: '100%', mb: 1 }}>
              {currentFolder ? (
                <FolderBreadcrumbs
                  allItems={() => updateCurrentFolder(network.showFolderTree.id)}
                  label={t('pages.devices.dialogs.play_content_configure.all_folders')}
                  changeFolder={(id) => updateCurrentFolder(id)}
                  folder={currentFolder}
                />
              ) : (
                <div>{t('pages.devices.dialogs.play_content_configure.all_folders')}</div>
              )}
            </Box>
            <Box sx={{ height: 320, overflow: 'auto' }}>
              <RadioGroup
                aria-label={t('pages.devices.dialogs.play_content_configure.folder_label')}
                value={folderId}
                onChange={(e) => setSelectedFolderId(e.target.value)}
              >
                <List>
                  {(currentFolder?.children || accessibleFolders).map((x) => (
                    <ShowFolderListItem key={x.id} folder={x} onClick={handleFolderClick} />
                  ))}
                </List>
              </RadioGroup>
            </Box>
          </FolderContextProvider>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={loading}>
          {t('common.dialogs.cancel')}
        </Button>
        {activeStep > 0 && (
          <Button
            variant="outlined"
            onClick={() => setActiveStep((step) => (step - 1) as 0 | 1 | 2)}
            disabled={loading}
          >
            {t('pages.devices.dialogs.play_content_configure.back')}
          </Button>
        )}
        {activeStep < 2 ? (
          <Button
            color="primary"
            variant="contained"
            disabled={activeStep === 0 && pickedIds.length === 0}
            onClick={() => setActiveStep((step) => (step + 1) as 0 | 1 | 2)}
          >
            {t('pages.devices.dialogs.play_content_configure.next')}
          </Button>
        ) : (
          <LoadingButton
            color="primary"
            variant="contained"
            disabled={!selectedFolderId || !name.trim()}
            loading={loading}
            onClick={handlePlay}
          >
            {t('pages.devices.dialogs.play_content_configure.play')}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};
